<template>
  <div id="MediaPlayer">
    <video id="video"
      class="video-js vjs-big-play-centered"
      x-webkit-airplay="allow"
      webkit-playsinline>
      </video>
      <Question v-if="showQuestion" :title="markers[nextQuestionIndex] && markers[nextQuestionIndex].title" :questionOptions="markers[nextQuestionIndex] && markers[nextQuestionIndex].questionOptions" @submit="submitAnswer"></Question>
      <Score v-if="showScore" @hideScore="hideScore"></Score>
    </div>
</template>
<script>
import Question from '@/views/record/detail/Question'
import Score from '@/views/record/detail/Score'
import videojs from 'video.js'

export default {
  props: {
    cover: {
      type: String,
      required: true,
    },
    origUrl: {
      type: String,
      required: true,
    },
    pauseTime: {
      type: Number,
    },
  },
  components: { Question, Score },
  data () {
    return {
      player: null,
      config: {
        controls: true, // 是否显示控制条
        autoplay: false, // 是否自动播放(ios不支持自动播放)
        preload: 'auto',
        /*
        'auto'预加载视频（需要浏览器允许）;
        'metadata'仅预加载视频meta信息;
        'none'不预加载;
        */
        poster: this.cover, // 视频播放前显示的图片
        loop: false, // 是否循环播放
        width: 880, // 设置播放器宽度
        height: 495, // 设置播放器高度
        techOrder: ['html5', 'flash'], // 优先使用的播放模式
        streamTimeoutTime: 30 * 1000, // 拉流超时时间，默认30s
        errorDisplay: false,
        controlBar: {
          pictureInPictureToggle: false,
          currentTimeDisplay: true,
          timeDivider: true,
          durationDisplay: true,
          remainingTimeDisplay: false,
          seekToLive: false,
          playbackRateMenuButton: {
            playbackRates: [0.5, 1, 1.5, 2],
          },
        },
        playbackRates: [0.5, 1, 1.5, 2],
      },
      videoDuration: 0,
      markers: [],
      oldTime: 0,
      maxTime: 0,
      nextQuestionIndex: -1,
      showQuestion: false,
      showScore: false,
      answeredQustionNum: 0, // 答过题目的总数
    }
  },
  methods: {
    initPlayer () {
      // 防止重复初始化
      if (this.player) return false
      const that = this
      let player = videojs('video', this.config, () => {
        player.src([
          { src: that.origUrl, type: 'video/mp4' },
          { src: that.origUrl, type: 'video/mov' },
        ])
        this.initErrorComponent(player)
      })
      this.initEventListener(player)
      this.player = player
    },
    initErrorComponent (player) {
      let Component = videojs.getComponent('Component')
      const ErrorComponent = videojs.extend(Component, {
        createEl: function () {
          let component = document.createElement('div')
          component.classList = 'error-display-wrapper'
          let inner = document.createElement('div')
          inner.classList = 'error-display'
          component.appendChild(inner)
          return component
        },
      })
      player.addChild(new ErrorComponent(), {})
      player.on('error', function (err) {
        let { errCode, errMsg } = err
        switch (errCode) {
          case 1:
            errMsg = '您中止了媒体播放'
            break
          case 2:
            errMsg = '网络错误导致媒体下载部分失败'
            break
          case 3:
            errMsg = '由于损坏问题，媒体播放被中止，或者因为媒体使用了浏览器不支持的功能'
            break
          case 4:
            errMsg = '由于服务器或网络故障或格式不受支持，无法加载媒体'
            break
          case 5:
            errMsg = '媒体已加密，我们没有解密它的密钥'
            break
        }
        document.querySelector('.error-display').innerHTML = errMsg
      })
    },
    initEventListener (player) {
      player.on('play', () => {
        this.$emit('play')
        let newTime = this.player.currentTime()
        if (newTime > this.maxTime) {
          this.player.currentTime(this.oldTime)
        }
      })
      player.on('pause', () => {
        this.$emit('pause')
        if (this.player.currentTime() <= this.maxTime + 1) {
          this.oldTime = this.player.currentTime()
        }
      })
      player.on('ended', () => {
        this.$emit('ended')
        if (this.answeredQustionNum < this.markers.length) {
          this.showScore = true
          this.answeredQustionNum = this.markers.length
        }
      })
      player.on('timeupdate', () => {
        this.checkIsStop()
      })
      player.on('loadedmetadata', () => { // 加载完毕之后获取视频长度
        this.videoDuration = player.duration()
        this.maxTime = player.duration()
        this.getQuestionBank()
      })
      // 阻止视频区域的右键点击
      document.getElementById('MediaPlayer').addEventListener('contextmenu', ev => {
        event.preventDefault()
      })
    },
    seekTo (player) {
      if (this.pauseTime !== 0) {
        player.seekTo(this.pauseTime * 1000)
      }
    },
    getQuestionBank () {
      this.$store.dispatch('course/getQuestionBank', { pkid: this.$route.params.videoId }).then(res => {
        this.markers = res.filter((item) => {
          return item.enable === 0
        })
        this.initQuestionMarker()
      })
    },
    // 问题打点
    initQuestionMarker () {
      let progressBar = document.getElementsByClassName('vjs-progress-holder')[0]
      let markerListInnerHTML = ''
      this.markers.forEach((item, index) => {
        markerListInnerHTML = markerListInnerHTML + `<div class="marker-div" style="left:${(item.pointTime / this.videoDuration) * 100}%"></div>`
        // 判断第一个未答题的位置
        if (this.maxTime === this.videoDuration && item.enable === 0) {
          this.nextQuestionIndex = index
          this.maxTime = item.pointTime
        }
        if (item.enable === 1) {
          this.answeredQustionNum = this.answeredQustionNum + 1
        }
      })
      // 一次性把点append到进度条上
      let div = document.createElement('div')
      div.className = 'marker-wrap'
      div.innerHTML = markerListInnerHTML
      progressBar.appendChild(div)
      this.progressBarAddEvent()
    },
    progressBarAddEvent () {
      let progressBar = document.getElementsByClassName('vjs-progress-control')[0]
      progressBar.addEventListener('mousedown', () => {
        if (this.player.getCache().currentTime < this.maxTime) { // 判断播放时间改变之前的时间
          this.oldTime = this.player.getCache().currentTime
        }
      }, true)
      progressBar.addEventListener('mouseup', () => {
        let newTime = this.player.currentTime()
        if (newTime > this.maxTime) {
          this.player.currentTime(this.oldTime)
        }
      }, true)
    },
    checkIsStop () {
      if (this.player.currentTime() >= this.maxTime) {
        if (this.player.currentTime() <= this.maxTime + 1 && this.markers[this.nextQuestionIndex]) {
          this.player.pause()
          this.showQuestion = true
          this.exitFullscreen()
        }
      }
    },
    // 退出全屏
    exitFullscreen () {
      let de = document
      if (de.fullscreenElement || de.webkitFullscreenElement || de.mozFullScreenElement) {
        if (de.exitFullscreen) {
          de.exitFullscreen()
        } else if (de.mozCancelFullScreen) {
          de.mozCancelFullScreen()
        } else if (de.webkitCancelFullScreen) {
          de.webkitCancelFullScreen()
        }
      }
    },
    submitAnswer (questionOptionsId, isAnswer) {
      let params = {
        pkid: this.$route.params.videoId,
        questionBankId: this.markers[this.nextQuestionIndex].id,
        questionOptionsId,
        isAnswer,
      }
      this.$store.dispatch('course/submitAnswer', params).then(res => {
        setTimeout(() => {
          let markerDivs = document.getElementsByClassName('marker-div')
          markerDivs[this.nextQuestionIndex].className = 'marker-div-passed marker-div'
          this.markers[this.nextQuestionIndex].enable = 1
          this.nextQuestionIndex++
          this.maxTime = this.markers[this.nextQuestionIndex] ? this.markers[this.nextQuestionIndex].pointTime : this.videoDuration
          this.showQuestion = false
          this.player.play()
        }, 3000)
      })
    },
    hideScore () {
      this.showScore = false
    },
  },
  mounted () {
    this.initPlayer()
  },
  beforeDestroy () {
    this.$emit('beforeDestroy', this.player)
    if (this.player) {
      this.player.pause()
      // 先暂停再释放资源，否则会出现一直请求资源的问题，这里加了延迟操作
      setTimeout(() => {
        this.player.dispose()
        this.player = null
      }, 0)
    }
  },
}
</script>
<style lang="scss">
.error-display-wrapper {
  position: relative;
  height: 100%;
  width: 100%;
  background: linear-gradient(180deg,rgba(0,0,0,.8),hsla(0,0%,100%,0));
  display: none;
}
.error-display {
  text-align: center;
  color:#fff;
  font-size: 16px;
  padding: 20px 10px;
}
/*  前面提到，播放器出现错误时，我们的播放容器div会添加class vjs-error
 *  这里我们使用这个类来作为错误信息的显示前提
 */
.vjs-error .error-display-wrapper {
    display: block;
}
.vjs-time-divider {
  display: block;
}
.video-js {
  width: 100%;
  height: 100%;
}
.video-js .vjs-current-time {
  display: block;
  padding: 0;
}
.video-js .vjs-duration {
  display: block;
  padding: 0;
}

.marker-div {
  position: absolute;
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: #fff;
  top: -1px;
}
.marker-div-passed {
  display: none;
}
.video-js .vjs-progress-control {
  padding-left: 40px !important;
}
.video-js .vjs-progress-holder {
  height: 1px !important;
}
.video-js .vjs-play-progress:before{
  top: -6px !important;
}
.video-js .vjs-time-control {
  padding: 0 !important;
}
.progress-time {
  position: absolute !important;
  left: 0;
}
</style>
